import type { FC } from 'react';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Tooltip } from '@interstate/components/Tooltip';
import { useCustomerCardSelector } from '../hooks/useCustomerCardSelector';
import { useIsChangeCustomerCtaVisible } from '../hooks/useIsChangeCustomerCtaVisible';
import * as customerCardSelectors from '../store/selectors';
import { useCustomerCardContext } from './CustomerFormContext';
import { CARD_OPERATION_KIND, CONTRACT_GENERATED_UNAVAILABLE_REASON } from '../constants';
import { CUSTOMER_TABS, type CustomerTabKey } from '../types';

const CHANGE_OPERATION = {
    [CUSTOMER_TABS.BUYER]: {
        label: 'Change Buyer',
        kind: CARD_OPERATION_KIND.CHANGE_BUYER,
        tooltip:
            'Use Change Buyer when you need to replace the buyer with a different customer. ' +
            'Editing buyer information updates the current buyer only.'
    },
    [CUSTOMER_TABS.CO_BUYER]: {
        label: 'Change Co-Buyer',
        kind: CARD_OPERATION_KIND.CHANGE_CO_BUYER,
        tooltip:
            'Use Change Co-Buyer when you need to replace the co-buyer with a different customer. ' +
            'Editing co-buyer information updates the current co-buyer only.'
    }
} as const;

export interface ChangeCustomerCtaProps {
    customerRole: CustomerTabKey;
    /** A read-only card offers no entry points. */
    isLocked?: boolean;
}

/**
 * Entry point for replacing the participant whose form this is. Opens the card onto the operation's
 * search step; the search panel and the apply path take it from there.
 */
const ChangeCustomerCta: FC<ChangeCustomerCtaProps> = ({ customerRole, isLocked }) => {
    const theme = useInterstateTheme();
    const isChangeCustomerCtaVisible = useIsChangeCustomerCtaVisible(isLocked);
    const isContractGenerated = useCustomerCardSelector(customerCardSelectors.getIsContractGenerated);
    const { onRequestOperation } = useCustomerCardContext();

    if (!isChangeCustomerCtaVisible) return null;

    const { label, kind, tooltip } = CHANGE_OPERATION[customerRole];

    const ctaButton = (
        <Button
            data-testid={`change-customer-cta-${customerRole}`}
            buttonStyle="tertiary"
            size="small"
            sx={{ padding: 0 }}
            disabled={isContractGenerated}
            onClick={() => onRequestOperation?.(kind)}
        >
            {label}
        </Button>
    );

    return (
        // Sits at the far right of the section heading row, leaving the host's own slot in place.
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            {isContractGenerated ? (
                <Tooltip size="large" position="bottom" toolTipContent={CONTRACT_GENERATED_UNAVAILABLE_REASON}>
                    <span data-testid={`change-customer-cta-${customerRole}-disabled-tooltip`}>{ctaButton}</span>
                </Tooltip>
            ) : (
                ctaButton
            )}
            <Tooltip size="large" position="bottom" toolTipContent={tooltip}>
                <InformationCircleIcon
                    color={theme.tokens.ComponentBtnTextColor}
                    width={16}
                    height={16}
                    style={{ display: 'block' }}
                    data-testid={`change-customer-cta-${customerRole}-info`}
                />
            </Tooltip>
        </Box>
    );
};

export default ChangeCustomerCta;
